import {
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
  use,
  useMemo,
  memo,
  type CSSProperties,
} from "react";
import { Handle, Position } from "@xyflow/react";
import type { Node, NodeProps } from "@xyflow/react";
import { useStore } from "../App/store";
import { shallow } from "zustand/shallow";
import {
  sanitizeLabel,
  toggleBold,
  applyTextColor,
  removeTextColor,
  saveCursorPosition,
  restoreCursorPosition,
  FLAG_COLORS,
} from "../utils/textFormatting";
import { CiSquarePlus, CiSquareMinus } from "react-icons/ci";
import { BsCheckCircleFill } from "react-icons/bs";
import { NodeData, nodeType } from "../types/types";
import { showQuickLook } from "../utils/ShowQuickLook";
import { Image } from "primereact/image";
import { OverlayPanel } from "primereact/overlaypanel";
import ReactMarkdown from "react-markdown";
import { redirect } from "react-router-dom";
import { convertHtmlToMarkdown } from "../Helpers/NodeHelpers";
import { getCalculatedValue } from "../Helpers/Calculations";
import { RiCheckboxCircleFill } from "react-icons/ri";
import { BiTask } from "react-icons/bi";
import { LuGoal, LuCheck, LuPencil } from "react-icons/lu";
import { TbProgressCheck } from "react-icons/tb";
import { MdOutlineAddTask, MdOutlineBugReport } from "react-icons/md";
import { FaCheckSquare, FaHandshake } from "react-icons/fa";
import { IoWarningOutline, IoFlag } from "react-icons/io5";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
// import { SpeedDial } from "primereact/speeddial";
import { MenuItem } from "primereact/menuitem";
import { Tooltip } from "primereact/tooltip";
import { Menu } from "primereact/menu";
import UnsplashImagePicker from "./unsplashImagePicker";
import EmojiPickerPopup from "./EmojiPickerPopup";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import KanbanCard from "./KanbanCard";
import ResourceDialog from "./ResourceDialog";
import RiskDialog from "./RiskDialog";
import DecisionDialog from "./DecisionDialog";
import AddressCard from "./AddressCard";
import {
  MdPerson,
  MdLocationOn,
  MdWarning,
  MdCheckCircle,
  MdAccountTree,
  MdRemoveCircleOutline,
  MdImage,
} from "react-icons/md";

function NodeItem({
  id,
  data,
  isConnectable,
  selected,
}: NodeProps<Node<NodeData>>) {
  const inputRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const noteDetailsRef = useRef<HTMLDivElement>(null);
  const presentationOrderInputRef = useRef<HTMLInputElement>(null);
  const priorityInputRef = useRef<HTMLInputElement>(null);
  const valueInputRef = useRef<HTMLInputElement>(null);
  const op = useRef<OverlayPanel>(null);
  const setSelectedNodeId = useStore((state) => state.setSelectedNodeId);
  const setSelectedNode = useStore((state) => state.setSelectedNode);
  const setShowNotesModal = useStore((state) => state.setShowNotesModal);
  const setCurrentNotes = useStore((state) => state.setCurrentNotes);
  const setCurrentLinks = useStore((state) => state.setCurrentLinks);
  const setCurrentNodeLabel = useStore((state) => state.setCurrentNodeLabel);
  const noteDetailsNodeId = useStore((state) => state.noteDetailsNodeId);
  const setNoteDetailsNodeId = useStore((state) => state.setNoteDetailsNodeId);
  const showDetails = noteDetailsNodeId === id;
  const isNodeSelected = useStore((state) => state.selectedNodeId) === id;
  const getNode = useStore((state) => state.getNode);
  const nodes = useStore((state) => state.nodes);
  const activeDrilldownTab = useStore((state) => state.activeDrilldownTab);
  const drilldownTabs = useStore((state) => state.drilldownTabs);
  const openDrilldownTab = useStore((state) => state.openDrilldownTab);
  const setActiveDrilldownTab = useStore(
    (state) => state.setActiveDrilldownTab,
  );
  const setPendingFocusNodeId = useStore(
    (state) => state.setPendingFocusNodeId,
  );
  const [isTask, setIsTask] = useState<boolean>(data.task?.title?.length > 0);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [showSpeedDial, setShowSpeedDial] = useState<boolean>(false);
  const [speedDialVisible, setSpeedDialVisible] = useState<boolean>(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showResourceDialog, setShowResourceDialog] = useState(false);
  const [showAddressCard, setShowAddressCard] = useState(false);
  const [showRiskDialog, setShowRiskDialog] = useState(false);
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [showUnsplashPicker, setShowUnsplashPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const imageMenuRef = useRef<Menu>(null);
  const updateNodeData = useStore((state) => state.updateNodeData);
  const showToast = useStore((state) => state.showToast);

  // Debug: log when data changes for this node
  useEffect(() => {
    if (id === "CuzamD0EHaED0RiUMh9o4") {
      console.log(`[DEBUG] Node ${id} data updated: type="${data.type}"`);
    }
  }, [data.type, id]);

  // Helper function to check if node is from MS365
  const isMS365Node = useMemo(() => {
    return data.links?.some((link) => link.title === "MS365 Source") || false;
  }, [data.links]);

  // Helper function to get MS365 link
  const getMS365Link = () => {
    return data.links?.find((link) => link.title === "MS365 Source");
  };

  // Helper function to get icon component from node type
  const getIconFromType = (
    type: string | undefined,
  ): React.ReactNode | null => {
    if (!type) return null;
    switch (type) {
      case "task":
        // Show status-specific icon for tasks
        if (data.task?.status?.toLowerCase() === "done") {
          return <LuCheck size={16} />;
        } else if (
          data.task?.status?.toLowerCase() === "doing" ||
          data.task?.status?.toLowerCase() === "inprogress"
        ) {
          return <TbProgressCheck size={16} />;
        } else if (data.task?.status?.toLowerCase() === "testing") {
          return <MdOutlineBugReport size={16} />;
        } else if (data.task?.status?.toLowerCase() === "impediment") {
          return <IoWarningOutline size={16} />;
        } else {
          return <MdOutlineAddTask size={16} />;
        }
      case "resource":
        return <MdPerson size={16} />;
      case "location":
        return <MdLocationOn size={16} />;
      case "risk":
        return <MdWarning size={16} />;
      case "decision":
        return <FaHandshake size={16} />;
      default:
        return null;
    }
  };

  // Helper function to get tooltip content
  const getTooltipContent = (): string => {
    if (!data.type) return "";

    const typeLabel = data.type.charAt(0).toUpperCase() + data.type.slice(1);

    if (data.type === "task" && data.task?.status) {
      return `${typeLabel}\n${data.task.status}\n(doubleclick to edit)`;
    }

    return `${typeLabel}\n(doubleclick to edit)`;
  };

  // Derive icon directly from data - no memoization to ensure it's always fresh
  const selectedSpeedDialIcon = (() => {
    if (!data.type) {
      // console.log(`[${data.label}] Icon: null (no type)`);
      return null;
    }

    // For task type, show status-specific icon
    if (data.type === "task") {
      const status = data.task?.status?.toLowerCase();
      // console.log(`[${data.label}] Icon: task with status="${status}"`);
      if (status === "done") {
        return <LuCheck size={16} />;
      } else if (status === "doing" || status === "inprogress") {
        return <TbProgressCheck size={16} />;
      } else if (status === "testing") {
        return <MdOutlineBugReport size={16} />;
      } else if (status === "impediment") {
        return <IoWarningOutline size={16} />;
      } else {
        return <MdOutlineAddTask size={16} />;
      }
    }

    // For non-task types, get the icon
    // console.log(`[${data.label}] Icon: getIconFromType("${data.type}")`);
    return getIconFromType(data.type);
  })();

  // Tooltip for type icon intentionally disabled (type selection moved to context menu)

  // Add tooltips to action buttons
  useEffect(() => {
    // Use a small delay to ensure action buttons are rendered
    const timeoutId = setTimeout(() => {
      const actionButtons = wrapperRef.current
        ?.closest(".react-flow__node")
        ?.querySelectorAll(".node-speeddial-root .p-speeddial-action");

      if (actionButtons && actionButtons.length > 0) {
        const labels = [
          "Clear Type",
          "Task",
          "Resource",
          "Location",
          "Risk",
          "Decision",
        ];
        actionButtons.forEach((button, index) => {
          if (labels[index]) {
            button.setAttribute("data-pr-tooltip", labels[index]);
            button.setAttribute("data-pr-position", "top");
            button.setAttribute("data-pr-at", "center top");
          }
        });
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [isHovered, selectedSpeedDialIcon]);

  // Add tooltip to MS365 icon
  useEffect(() => {
    if (isMS365Node) {
      const ms365Icon = wrapperRef.current
        ?.closest(".react-flow__node")
        ?.querySelector(".node-ms365-icon");

      if (ms365Icon) {
        ms365Icon.setAttribute(
          "data-pr-tooltip",
          "MS365\n(doubleclick to open)",
        );
        ms365Icon.setAttribute("data-pr-position", "top");
        ms365Icon.setAttribute("data-pr-at", "center top");
      }
    }
  }, [isMS365Node, data.icon]);

  // Handle delayed speed dial appearance
  useEffect(() => {
    if (isHovered) {
      // Set a delay before showing the speed dial
      hoverTimeoutRef.current = setTimeout(() => {
        setShowSpeedDial(true);
      }, 300); // 300ms delay
    } else {
      // Clear timeout if mouse leaves before delay completes
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      // Hide speed dial immediately when mouse leaves
      setShowSpeedDial(false);
      setSpeedDialVisible(false);
    }

    // Cleanup on unmount
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [isHovered]);

  // Sync speedDialVisible with showSpeedDial
  useEffect(() => {
    if (showSpeedDial) {
      setSpeedDialVisible(true);
    }
  }, [showSpeedDial]);

  // Handler for changing node type with task confirmation
  const handleTypeChange = (
    newType: nodeType | null,
    newIcon: React.ReactNode | null,
  ) => {
    console.log(
      `handleTypeChange called: newType="${newType}", currentType="${data.type}"`,
    );
    // If currently a task and changing to a different type, show confirmation
    if (data.type === "task" && newType !== "task" && isTask) {
      setConfirmDialogVisible(true);
      // Store the pending change
      (window as any).pendingTypeChange = { newType, newIcon };
    } else {
      // Direct change - no confirmation needed
      if (newType === null) {
        // Clear the type
        console.log(`Clearing type for node ${id}`);
        updateNodeData(id, { type: undefined });
        // If the node had a task, remove it when clearing the type
        if (data.task?.id) {
          console.log(`Removing task object for node ${id}`);
          updateNodeTask(id, false, data.label);
          setIsTask(false);
        }
      } else {
        console.log(`Setting type to "${newType}" for node ${id}`);
        updateNodeData(id, { type: newType });
        if (newType === "task") {
          // Create task if setting to task type
          if (!isTask) {
            console.log(`Creating task for node ${id}`);
            updateNodeTask(id, true, data.label);
            setIsTask(true);
          }
        } else {
          // Remove task object when setting to any non-task type
          if (data.task?.id) {
            console.log(
              `Removing task object for node ${id} (switching to non-task type)`,
            );
            updateNodeTask(id, false, data.label);
            setIsTask(false);
          }
        }
      }
      // Close speeddial after selection
      setSpeedDialVisible(false);
    }
  };

  // SpeedDial items for project planning
  const speedDialItems: MenuItem[] = [
    {
      icon: <MdRemoveCircleOutline size={20} />,
      command: () => {
        console.log("Clear type clicked");
        handleTypeChange(null, null);
      },
    },
    {
      icon: <MdOutlineAddTask size={20} />,
      command: () => {
        console.log("Task clicked");
        handleTypeChange("task", <MdOutlineAddTask size={16} />);
      },
    },
    {
      icon: <MdPerson size={20} />,
      command: () => {
        console.log("Resource clicked");
        handleTypeChange("resource", <MdPerson size={16} />);
      },
    },
    {
      icon: <MdLocationOn size={20} />,
      command: () => {
        console.log("Location clicked");
        handleTypeChange("location", <MdLocationOn size={16} />);
      },
    },
    {
      icon: <MdWarning size={20} />,
      command: () => {
        console.log("Risk clicked");
        handleTypeChange("risk", <MdWarning size={16} />);
      },
    },
    {
      icon: <FaHandshake size={20} />,
      command: () => {
        console.log("Decision clicked");
        handleTypeChange("decision", <FaHandshake size={16} />);
      },
    },
  ];

  // Check if node is disabled (from Focus Filtering)
  const currentNode = useMemo(() => getNode(id), [getNode, id]);
  const isDisabled = useMemo(
    () => currentNode?.draggable === false,
    [currentNode?.draggable],
  );
  const isDisabledByFilter = useMemo(
    () => (currentNode?.data as any)?.isDisabledByFilter === true,
    [currentNode?.data],
  );

  useLayoutEffect(() => {
    if (inputRef.current && wrapperRef.current) {
      wrapperRef.current.style.width = inputRef.current.style.width;
      wrapperRef.current.style.height = inputRef.current.style.height;
    }
  }, [inputRef.current?.style.width, inputRef.current?.style.height]);

  const updateNodeLabel = useStore((state) => state.updateNodeLabel);
  const updateNodeTask = useStore((state) => state.updateNodeTask);
  const updatePresentationOrder = useStore(
    (state) => state.updatePresentationOrder,
  );
  const updatePriority = useStore((state) => state.updatePriority);
  const updateValue = useStore((state) => state.updateValue);
  const focusValue = useStore((state) => state.focusValue);
  const cursorPositionRef = useRef<number | null>(null);
  const isComposingRef = useRef<boolean>(false);
  const isFocusedRef = useRef<boolean>(false);
  const skipCursorRestore = useRef<boolean>(false);
  const isPresentationMode = useStore((state) => state.isPresentationMode);
  const presentWithShowHideButtons = useStore(
    (state) => state.presentWithShowHideButtons,
  );

  // Handler for task updates from KanbanCard
  const handleTaskUpdate = (
    taskId: string | number,
    title: string,
    nodeTitle: string,
    status: string,
    summary: string,
    priority?: string,
    tags?: string,
    assignee?: string,
    estimate?: number,
    rankId?: number,
    color?: string,
    className?: string,
    startDate?: Date,
    endDate?: Date,
  ) => {
    // takeSnapshot(); // TODO: Add undo/redo support
    const taskData = {
      task: {
        id: taskId,
        nodeTitle: title, // Use title for nodeTitle as well
        title: title,
        status: status,
        summary: summary,
        priority: priority,
        tags: tags,
        assignee: assignee,
        estimate: estimate,
        rankId: rankId,
        color: color,
        className: className,
        startDate: startDate,
        endDate: endDate,
      },
    };
    updateNodeData(id, taskData);
    setIsTask(true);
  };

  // Handler for task deletion
  const handleTaskDelete = (taskId: string | number) => {
    // takeSnapshot(); // TODO: Add undo/redo support
    updateNodeTask(id, false, "");
    setIsTask(false);
  };

  // Handler for image URL update
  const handleImageUrlSave = () => {
    if (imageUrl.trim()) {
      // takeSnapshot(); // TODO: Add undo/redo support
      updateNodeData(id, { icon: imageUrl.trim() });
      // Icon will automatically update through the useEffect that watches data.icon
    }
    setShowImageDialog(false);
    setImageUrl("");
  };

  // Handler for resource updates
  const handleResourceSave = (resourceData: {
    id?: string;
    name: string;
    role: string;
    department: string;
    email: string;
    phone: string;
    availability: number;
    skills: string;
    notes: string;
  }) => {
    // takeSnapshot(); // TODO: Add undo/redo support
    updateNodeData(id, {
      resource: resourceData,
    });
  };

  // Handler for resource deletion
  const handleResourceDelete = () => {
    // takeSnapshot(); // TODO: Add undo/redo support
    updateNodeData(id, { resource: undefined });
    setShowResourceDialog(false);
  };

  // Handler for address/location updates
  const handleAddUpdateAddress = (
    addressId: string,
    nodeTitle: string,
    name: string,
    street: string,
    zip_code: string,
    city: string,
    Phone: string,
    email: string,
    vat: string,
    employees: string,
    notes: string,
    latitude?: number,
    longitude?: number,
  ) => {
    // takeSnapshot(); // TODO: Add undo/redo support
    updateNodeData(id, {
      address: {
        id: addressId || id,
        nodeTitle,
        name,
        street,
        zip_code,
        city,
        Phone,
        email,
        vat,
        employees,
        notes,
        latitude,
        longitude,
      },
    });
  };

  // Handler for address deletion
  const handleDeleteAddress = (addressId: string) => {
    // takeSnapshot(); // TODO: Add undo/redo support
    updateNodeData(id, { address: undefined });
    setShowAddressCard(false);
  };

  // Handler for risk updates
  const handleRiskSave = (riskData: {
    id?: string;
    name: string;
    description: string;
    category: string;
    impact: string;
    likelihood: string;
    severity: string;
    mitigation: string;
    owner: string;
    status: string;
    identifiedDate?: Date | string;
    reviewDate?: Date | string;
  }) => {
    // takeSnapshot(); // TODO: Add undo/redo support
    updateNodeData(id, {
      risk: riskData,
    });
  };

  // Handler for risk deletion
  const handleRiskDelete = () => {
    // takeSnapshot(); // TODO: Add undo/redo support
    updateNodeData(id, { risk: undefined });
    setShowRiskDialog(false);
  };

  // Handler for decision save
  const handleDecisionSave = (decisionData: {
    id?: string;
    name: string;
    description: string;
    category: string;
    status: string;
    decisionMaker: string;
    stakeholders: string;
    criteria: string;
    alternatives: string;
    selectedOption: string;
    rationale: string;
    implications: string;
    decisionDate?: Date | string;
    reviewDate?: Date | string;
  }) => {
    // takeSnapshot(); // TODO: Add undo/redo support
    updateNodeData(id, {
      decision: decisionData,
    });
  };

  // Handler for decision deletion
  const handleDecisionDelete = () => {
    // takeSnapshot(); // TODO: Add undo/redo support
    updateNodeData(id, { decision: undefined });
    setShowDecisionDialog(false);
  };

  // Handler for image menu options
  const handleImageFromUnsplash = () => {
    setShowUnsplashPicker(true);
  };

  const handleImageFromUrl = () => {
    setShowImageDialog(true);
  };

  const handleImageFromEmoji = () => {
    setShowEmojiPicker(true);
  };

  const handleEmojiSelect = (emojiData: any) => {
    updateNodeData(id, { icon: emojiData.emoji });
    setShowEmojiPicker(false);
  };

  const handleRemoveImage = () => {
    updateNodeData(id, { icon: "" });
  };

  const imageMenuItems: MenuItem[] = [
    {
      label: "From Unsplash",
      icon: "pi pi-images",
      command: handleImageFromUnsplash,
    },
    {
      label: "From URL",
      icon: "pi pi-link",
      command: handleImageFromUrl,
    },
    {
      label: "Emoji",
      icon: "pi pi-face-smile",
      command: handleImageFromEmoji,
    },
    {
      separator: true,
    },
    {
      label: "Remove Image",
      icon: "pi pi-times",
      command: handleRemoveImage,
      disabled: !data.icon || data.icon.length === 0,
    },
  ];

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    isFocusedRef.current = true;
    const key = event.currentTarget.getAttribute("node-identifier");
    setSelectedNodeId(key);
    // Select all text in contenteditable
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNodeContents(event.currentTarget);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    setSelectedNodeId(null);
  };

  const handleClick = (
    event: React.MouseEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const key = event.currentTarget.getAttribute("node-identifier");
    setSelectedNodeId(key);
  };

  const handleDetailsClick = (event: React.MouseEvent<Element>): void => {
    event.stopPropagation();
    setNoteDetailsNodeId(showDetails ? null : id);
  };

  // Adjust note details position if it overflows viewport
  useEffect(() => {
    if (showDetails && noteDetailsRef.current) {
      const rect = noteDetailsRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustLeft = 0;
      let adjustTop = 0;

      // Check right overflow
      if (rect.right > viewportWidth) {
        adjustLeft = viewportWidth - rect.right - 10; // 10px padding
      }

      // Check left overflow
      if (rect.left < 0) {
        adjustLeft = -rect.left + 10; // 10px padding
      }

      // Check bottom overflow
      if (rect.bottom > viewportHeight) {
        adjustTop = viewportHeight - rect.bottom - 10; // 10px padding
      }

      // Check top overflow
      if (rect.top < 0) {
        adjustTop = -rect.top + 10; // 10px padding
      }

      // Apply adjustments
      if (adjustLeft !== 0) {
        noteDetailsRef.current.style.left = `${-10 + adjustLeft}px`;
      }
      if (adjustTop !== 0) {
        noteDetailsRef.current.style.marginTop = `${8 + adjustTop}px`;
      }
    }
  }, [showDetails]);

  // Removed auto-focus to allow ReactFlow's built-in keyboard navigation to work
  // useEffect(() => {
  //   setTimeout(() => {
  //     inputRef.current?.focus({ preventScroll: true });
  //   }, 1);
  // }, []);

  useLayoutEffect(() => {
    if (inputRef.current) {
      const minWidth = 50;
      const padding = 10;

      inputRef.current.setAttribute("spellcheck", "false");

      // Only update innerHTML if not currently focused (to avoid cursor jumping during typing)
      if (!isFocusedRef.current && inputRef.current.innerHTML !== data.label) {
        inputRef.current.innerHTML = data.label;
      }

      // Reset height to auto to get the correct scrollHeight
      inputRef.current.style.height = "auto";

      // Set height based on content
      const scrollHeight = inputRef.current.scrollHeight;
      inputRef.current.style.height = `${scrollHeight}px`;

      // For multi-line content, measure the width of the longest line
      // Create a temporary span to measure each line separately
      const textContent = inputRef.current.textContent || "";
      const lines = textContent.split("\n");

      let maxLineWidth = minWidth;

      if (lines.length > 1) {
        // Multi-line: measure each line
        const tempSpan = document.createElement("span");
        tempSpan.style.visibility = "hidden";
        tempSpan.style.position = "absolute";
        tempSpan.style.whiteSpace = "nowrap";
        tempSpan.style.font = window.getComputedStyle(inputRef.current).font;
        document.body.appendChild(tempSpan);

        lines.forEach((line) => {
          tempSpan.textContent = line;
          const lineWidth = tempSpan.offsetWidth;
          if (lineWidth > maxLineWidth) {
            maxLineWidth = lineWidth;
          }
        });

        document.body.removeChild(tempSpan);
      } else {
        // Single line: use scrollWidth
        const originalWhiteSpace = inputRef.current.style.whiteSpace;
        inputRef.current.style.width = "auto";
        inputRef.current.style.whiteSpace = "nowrap";
        inputRef.current.offsetWidth; // trigger reflow
        maxLineWidth = inputRef.current.scrollWidth;
        inputRef.current.style.whiteSpace = originalWhiteSpace;
      }

      const calculatedWidth = Math.max(minWidth, maxLineWidth + padding);
      inputRef.current.style.width = `${calculatedWidth}px`;

      if (dragRef.current && wrapperRef.current) {
        dragRef.current.style.width = wrapperRef.current.offsetWidth + "px";
        dragRef.current.style.height = wrapperRef.current.offsetHeight + "px";
      }
    }
  }, [data.label]);

  const handleChange = (evt: React.FormEvent<HTMLDivElement>) => {
    // Save cursor position before update
    const cursorPos = saveCursorPosition(evt.currentTarget);
    const htmlContent = evt.currentTarget.innerHTML;

    // Adjust dimensions based on content
    const element = evt.currentTarget;
    const minWidth = 50;
    const padding = 10;

    // For multi-line content, measure the width of the longest line
    const textContent = element.textContent || "";
    const lines = textContent.split("\n");

    let maxLineWidth = minWidth;

    if (lines.length > 1) {
      // Multi-line: measure each line
      const tempSpan = document.createElement("span");
      tempSpan.style.visibility = "hidden";
      tempSpan.style.position = "absolute";
      tempSpan.style.whiteSpace = "nowrap";
      tempSpan.style.font = window.getComputedStyle(element).font;
      document.body.appendChild(tempSpan);

      lines.forEach((line) => {
        tempSpan.textContent = line;
        const lineWidth = tempSpan.offsetWidth;
        if (lineWidth > maxLineWidth) {
          maxLineWidth = lineWidth;
        }
      });

      document.body.removeChild(tempSpan);
    } else {
      // Single line: use scrollWidth
      const originalWhiteSpace = element.style.whiteSpace;
      element.style.width = "auto";
      element.style.whiteSpace = "nowrap";
      maxLineWidth = element.scrollWidth;
      element.style.whiteSpace = originalWhiteSpace;
    }

    // Adjust height
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;

    // Set width based on longest line
    const calculatedWidth = Math.max(minWidth, maxLineWidth + padding);
    element.style.width = `${calculatedWidth}px`;

    if (dragRef.current && wrapperRef.current) {
      dragRef.current.style.width = wrapperRef.current.offsetWidth + "px";
      dragRef.current.style.height = wrapperRef.current.offsetHeight + "px";
    }

    updateNodeLabel(id, htmlContent);

    // Restore cursor immediately (not in requestAnimationFrame) since we're preventing React from updating innerHTML
    // Skip restoration for natural editing operations like Enter key
    if (cursorPos && !skipCursorRestore.current) {
      restoreCursorPosition(element, cursorPos);
    }

    // Reset the flag
    skipCursorRestore.current = false;
  };

  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleCompositionEnd = (
    evt: React.CompositionEvent<HTMLDivElement>,
  ) => {
    isComposingRef.current = false;
  };

  const handleKeyDown = (evt: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle Enter key - insert <br/> tag instead of creating new block elements
    if (evt.key === "Enter" && !evt.shiftKey && !evt.ctrlKey && !evt.metaKey) {
      evt.preventDefault();
      // Insert a <br/> tag at cursor position
      document.execCommand("insertLineBreak");
      return;
    }

    // Handle text formatting shortcuts
    if (evt.ctrlKey || evt.metaKey) {
      if (evt.key === "b" || evt.key === "B") {
        evt.preventDefault();
        toggleBold();
        // Manually save the label after formatting
        if (inputRef.current) {
          const htmlContent = inputRef.current.innerHTML;
          updateNodeLabel(id, htmlContent);
        }
        return;
      }
      if (evt.key === "1") {
        evt.preventDefault();
        applyTextColor(FLAG_COLORS.green);
        // Manually save the label after applying color
        if (inputRef.current) {
          const htmlContent = inputRef.current.innerHTML;
          updateNodeLabel(id, htmlContent);
        }
        return;
      }
      if (evt.key === "2") {
        evt.preventDefault();
        applyTextColor(FLAG_COLORS.yellow);
        // Manually save the label after applying color
        if (inputRef.current) {
          const htmlContent = inputRef.current.innerHTML;
          updateNodeLabel(id, htmlContent);
        }
        return;
      }
      if (evt.key === "3") {
        evt.preventDefault();
        applyTextColor(FLAG_COLORS.red);
        // Manually save the label after applying color
        if (inputRef.current) {
          const htmlContent = inputRef.current.innerHTML;
          updateNodeLabel(id, htmlContent);
        }
        return;
      }
      if (evt.key === "0") {
        evt.preventDefault();
        removeTextColor();
        // Manually save the label after removing color
        if (inputRef.current) {
          const htmlContent = inputRef.current.innerHTML;
          updateNodeLabel(id, htmlContent);
        }
        return;
      }
    }

    // Shift+Enter also inserts <br/> (same as Enter now)
    if (evt.key === "Enter" && evt.shiftKey) {
      evt.preventDefault();
      document.execCommand("insertLineBreak");
      return;
    }

    // Cmd+Enter (Mac) or Ctrl+Enter to create child nodes
    if (evt.key === "Enter" && (evt.metaKey || evt.ctrlKey)) {
      evt.preventDefault();

      // Get current state from store
      const store = useStore.getState();
      const tabModeActive = store.tabModeActive;
      const tabModeParentId = store.tabModeParentId;

      // Determine the parent node for tab mode
      let parentNodeId = id;

      if (tabModeActive && tabModeParentId) {
        // Continue with existing parent
        parentNodeId = tabModeParentId;
      } else {
        // Start new tab mode with current node as parent
        store.setTabModeActive(true);
        store.setTabModeParentId(id);
      }

      // Create new child node
      const newNodeId = store.addChildNodeInTabMode(parentNodeId);

      // Redistribute all children evenly
      store.redistributeChildNodes(parentNodeId);

      // Focus the new node after a brief delay
      setTimeout(() => {
        const newInput = document.querySelector(
          `[node-identifier="${newNodeId}"]`,
        ) as HTMLInputElement;
        if (newInput) {
          newInput.focus({ preventScroll: true });
          newInput.select();
        }
      }, 50);
    } else if (evt.key === "Escape") {
      // Stop Tab mode on ESC key
      const store = useStore.getState();
      if (store.tabModeActive) {
        evt.preventDefault();
        store.setTabModeActive(false);
        store.setTabModeParentId(null);
      }
    }
  };

  const acceptRemoveTask = () => {
    setConfirmDialogVisible(false);
    updateNodeTask(id, false, "");
    setIsTask(false);

    // If there's a pending type change, execute it
    const pendingChange = (window as any).pendingTypeChange;
    if (pendingChange) {
      // If clearing type (null), explicitly use undefined to remove the property
      const newType =
        pendingChange.newType === null ? undefined : pendingChange.newType;
      updateNodeData(id, { type: newType });
      (window as any).pendingTypeChange = null;
    }
  };

  const handlePresentationOrder = (
    evt: React.ChangeEvent<HTMLInputElement>,
  ) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const parsedValue = parseInt(evt.target.value, 10);
    const value = isNaN(parsedValue) ? "" : parsedValue.toString();
    updatePresentationOrder(id, value);
  };

  const handlePriorityChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const value = evt.target.value;
    const isValid =
      value === "" || /^[A-Za-z]\d*$/.test(value) || /^\d+$/.test(value);
    if (isValid) {
      updatePriority(id, value);
    }
  };

  const handleValueChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const parsedValue = evt.target.value;
    updateValue(id, parsedValue);
  };

  const handleValueResult = (evt: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const parsedValue = evt.target.value;
    const floatValue = parseFloat(parsedValue);
    if (!isNaN(floatValue)) {
      updateValue(id, floatValue.toString());
    } else {
      updateValue(id, "");
    }
  };

  const hideChildNodes = useStore((state) => state.hideChildNodes);
  const showChildNodes = useStore((state) => state.showChildNodes);
  const [displayedNodeValue, setDisplayedNodeValue] = useState<
    React.ReactNode | undefined
  >();
  const doRollup = useStore((state) => state.doRollup);
  const [isNegativeValue, setIsNegativeValue] = useState(false);
  const activateShowValues = useStore((state) => state.showValues);
  const updateValues = useStore((state) => state.updateValues);

  const getValuesOutput = (values: string[], isRollup: boolean) => {
    return (
      <div className={`rollup-display ${isNegativeValue ? "negative" : ""}`}>
        <div className="rollup-container">
          {values.map((value, index) => {
            const hasSigma = value.includes("Σ");
            return (
              <div
                key={index}
                className={`${isRollup ? "rollup-value" : "regular-value"}${
                  hasSigma ? " has-sigma" : ""
                }`}
              >
                {value}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const displayValues: string[] = [];
    if (doRollup) {
      const rollupValues = data.rollupValues || [];
      let hasNonZeroValue = false;
      if (rollupValues) {
        rollupValues.forEach((rollup) => {
          const val = rollup?.value;
          // Check if value is meaningful (not zero, null, empty, etc.)
          const numericValue = typeof val === "string" ? parseFloat(val) : val;
          const isZero =
            val === null ||
            val === undefined ||
            val === "" ||
            val === "0" ||
            (typeof numericValue === "number" && numericValue === 0);
          // Also check if it's a string like "0 Σ" by parsing the numeric part
          const isZeroValue = isZero;

          if (rollup?.title && !isZeroValue) {
            displayValues.push(rollup.title + ": " + rollup.value);
            hasNonZeroValue = true;
          }
        });
      }
      const valuesOutput: React.ReactNode | undefined =
        displayValues.length > 0 && hasNonZeroValue
          ? getValuesOutput(displayValues, true)
          : undefined;
      setDisplayedNodeValue(valuesOutput);
    } else {
      const values = data.values || [];
      let hasNonZeroValue = false;
      if (values) {
        values.forEach((value) => {
          const val = value?.value;
          // Check if value is meaningful
          const numericValue = typeof val === "string" ? parseFloat(val) : val;
          const isZeroValue =
            !val ||
            val === "0" ||
            (typeof numericValue === "number" && numericValue === 0);

          if (value?.title && val && val.length > 0 && !isZeroValue) {
            displayValues.push(value.title + ": " + value.value);
            hasNonZeroValue = true;
          }
        });
      }
      const valuesOutput: React.ReactNode | undefined =
        displayValues.length > 0 && hasNonZeroValue
          ? getValuesOutput(displayValues, false)
          : undefined;
      setDisplayedNodeValue(valuesOutput);
    }
  }, [activateShowValues, data.rollupValues, data.values, doRollup]);

  useEffect(() => {
    //console.log("Node data updated:", data);
  }, [data.label, data.rollupValues, data.values]);

  // Use childrenMap and nodeMap for O(1) child lookups instead of O(n) filtering
  const { hasHiddenChildren, hasVisibleChildren } = useStore((state) => {
    const childIds = state.childrenMap.get(id) || [];
    let hidden = false;
    let visible = false;

    for (const childId of childIds) {
      const child = state.nodeMap.get(childId);
      if (child) {
        if (child.hidden) hidden = true;
        else visible = true;
        if (hidden && visible) break; // Early exit if both found
      }
    }

    return { hasHiddenChildren: hidden, hasVisibleChildren: visible };
  }, shallow);

  const handleHideShowClick = (event: React.MouseEvent) => {
    const showAllLevels = event.shiftKey;
    if (hasHiddenChildren) {
      showChildNodes(id, showAllLevels);
    } else {
      hideChildNodes(id);
      setTimeout(() => {
        document.getSelection()?.removeAllRanges();
        inputRef.current?.blur();
      }, 0);
    }
  };

  return (
    <>
      <Tooltip />
      <OverlayPanel ref={op}>
        {data.icon && (
          <>
            {/^https?:\/\//.test(data.icon) ||
            data.icon.startsWith("data:image/") ? (
              <img
                src={data.icon}
                alt="node-image"
                className="node-overlay-image"
              />
            ) : (
              <div className="node-overlay-emoji">{data.icon}</div>
            )}
          </>
        )}
      </OverlayPanel>
      <div
        key={id}
        className={`node-item-root${isDisabled ? " is-disabled" : ""}`}
        onContextMenu={
          isDisabled
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
              }
            : undefined
        }
        onClick={
          isDisabled
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
              }
            : undefined
        }
        onDoubleClick={
          isDisabled
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
              }
            : (e) => {
                e.stopPropagation();
                const div = inputRef.current;
                if (div) {
                  div.focus();
                  // Select all text in contenteditable
                  const range = document.createRange();
                  const selection = window.getSelection();
                  range.selectNodeContents(div);
                  selection?.removeAllRanges();
                  selection?.addRange(range);
                }
              }
        }
        onKeyDown={
          isDisabled
            ? (e) => {
                // Allow arrow keys for navigation even when disabled
                if (e.key.startsWith("Arrow") || e.key === "Tab") {
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
              }
            : undefined
        }
        onKeyUp={
          isDisabled
            ? (e) => {
                // Allow arrow keys for navigation even when disabled
                if (e.key.startsWith("Arrow") || e.key === "Tab") {
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
              }
            : undefined
        }
        onKeyPress={
          isDisabled
            ? (e) => {
                // Allow arrow keys for navigation even when disabled
                if (e.key.startsWith("Arrow") || e.key === "Tab") {
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
              }
            : undefined
        }
      >
        {(!isPresentationMode || presentWithShowHideButtons) && (
          <div>
            {hasVisibleChildren && !hasHiddenChildren && (
              <div className="show-hide-area">
                <CiSquareMinus
                  className="show-hide-icon show-hide-icon-minus"
                  onClick={handleHideShowClick}
                />
              </div>
            )}
            {hasHiddenChildren && (
              <div className="show-hide-area">
                <CiSquarePlus
                  className="show-hide-icon show-hide-icon-plus"
                  onClick={handleHideShowClick}
                />
              </div>
            )}
          </div>
        )}

        {/* Drill-down indicator - shows arrow based on context */}
        {(() => {
          // Check if this node is the focus of an active drill-down tab
          const isDrilldownFocus =
            activeDrilldownTab &&
            drilldownTabs.some(
              (tab) => tab.id === activeDrilldownTab && tab.nodeId === id,
            );

          const handleDrilldownIconClick = (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            // Open/switch to this node's drilldown tab
            openDrilldownTab(id);
          };

          const handleDrillupIconClick = (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            // After switching up to parent/Main, center on this node in that view.
            setPendingFocusNodeId(id);

            const currentTab = drilldownTabs.find(
              (t) => t.id === activeDrilldownTab,
            );
            const parentTabId = (currentTab as any)?.parentTabId ?? null;
            const parentExists = parentTabId
              ? drilldownTabs.some((t) => t.id === parentTabId)
              : false;

            setActiveDrilldownTab(parentExists ? parentTabId : null);
          };

          if (isDrilldownFocus) {
            // In drill-down view - show up arrow
            return (
              <div
                className="drilldown-indicator"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={handleDrillupIconClick}
              >
                <i
                  className="pi pi-arrow-up drilldown-icon drilldown-up"
                  title="Go to parent layer"
                />
              </div>
            );
          } else if (data.isDrilldownTab) {
            // Has active drill-down tab elsewhere - show down arrow
            return (
              <div
                className="drilldown-indicator"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={handleDrilldownIconClick}
              >
                <i
                  className="pi pi-arrow-down drilldown-icon"
                  title="Open drill-down tab"
                />
              </div>
            );
          }
          return null;
        })()}

        {focusValue === "presentation" && (
          <div className="presentation-order-area">
            <input
              id="presentation-order-input-field"
              className="presentation-order-input-field"
              spellCheck="false"
              value={data.presentationOrder}
              onChange={handlePresentationOrder}
              onFocus={handleFocus}
              ref={presentationOrderInputRef}
            />
          </div>
        )}

        {focusValue === "priorities" ? (
          <div className="priority-area">
            <input
              id="priority-input-field"
              className="priority-input-field"
              spellCheck="false"
              value={data.priority}
              onChange={handlePriorityChange}
              onFocus={handleFocus}
              ref={priorityInputRef}
            />
          </div>
        ) : (
          data.priority &&
          (focusValue === "prioritiesView" || focusValue === "all") && (
            <div className="priority-display">{data.priority}</div>
          )
        )}

        {focusValue === "rollup" ? (
          <div className="rollup-area">
            <input
              id="rollup-input-field"
              className="rollup-input-field"
              spellCheck="false"
              value={data.value}
              onChange={handleValueChange}
              onBlur={handleValueResult}
              onFocus={handleFocus}
              ref={valueInputRef}
            />
          </div>
        ) : null}

        {/* <div className="nodeitem-info-area"> */}
        {/* {data.complete && (focusValue === "done" || focusValue === "all") && ( */}
        {/* <div className="node-completed-indicator">
        <BsCheckCircleFill color="#a8ac5e" />
      </div> */}

        <ConfirmDialog
          id="task-confirmation-dialog"
          visible={confirmDialogVisible}
          onHide={() => {
            setConfirmDialogVisible(false);
            (window as any).pendingTypeChange = null;
          }}
          header="Confirmation"
          message="Removing the task will also remove the task title and all associated details. Are you sure you want to proceed?"
          icon="pi pi-exclamation-triangle"
          accept={acceptRemoveTask}
          reject={() => {
            setConfirmDialogVisible(false);
            (window as any).pendingTypeChange = null;
          }}
        />

        {useMemo(
          () => (
            <div
              style={
                {
                  "--node-drag-handle-bg":
                    focusValue == "all" ? data.backgroundColor : "white",
                } as CSSProperties
              }
              ref={dragRef}
              onClick={() => setSelectedNodeId(id)}
              className={`dragHandle node-dragHandle ${
                isNodeSelected ||
                selected ||
                document.querySelector(`[node-identifier="${id}"]`) ===
                  document.activeElement
                  ? "node-selected"
                  : ""
              } ${
                isDisabledByFilter
                  ? "draghandle-color-disabled"
                  : "draghandle-color-standard"
              }`}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {/* Connection handles are nested inside the dot so edges always
                  attach to its actual visual center/edges, regardless of the
                  label text's width. */}
              <Handle
                type="target"
                position={Position.Top}
                className="node-handle-center"
                isConnectable={isConnectable}
              />
              <Handle
                type="source"
                position={Position.Top}
                className="node-handle-center"
                isConnectable={isConnectable}
              />
              <Handle
                type="source"
                position={Position.Right}
                id="cross-link"
                className="node-crosslink-handle node-crosslink-handle-source"
              />
              <Handle
                type="target"
                position={Position.Left}
                id="cross-link"
                className="node-crosslink-handle node-crosslink-handle-target"
              />
            </div>
          ),
          [
            data.backgroundColor,
            focusValue,
            selected,
            id,
            isDisabledByFilter,
            isConnectable,
          ],
        )}

        {/*
          SpeedDial for project planning (disabled).
          Type selection was moved into the node right-click context menu.
          Keep a small icon where the SpeedDial button used to be, but ONLY when a type is selected.

          {(showSpeedDial || selectedSpeedDialIcon) &&
            !isDisabled &&
            !isMS365Node &&
            !isPresentationMode && (
              <div className="node-speeddial-container" ...>
                <SpeedDial ... />
              </div>
            )}
        */}

        {/* Badges row: all status/decoration icons for the node are collected
            here so they always appear as a single row, centered above the
            node regardless of how many of them are currently visible. */}
        <div className="node-top-badges-row">
          {selectedSpeedDialIcon &&
            !isDisabled &&
            !isMS365Node &&
            !isPresentationMode && (
              <div
                className="node-badge node-speeddial-container"
                onMouseEnter={() => !isDisabled && setIsHovered(true)}
                onClick={() => {
                  setSelectedNodeId(id);
                  const node = getNode(id);
                  if (node) setSelectedNode(node);
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  // Open the appropriate dialog based on the node type
                  if (data.type === "task") {
                    setShowTaskDialog(true);
                  } else if (data.type === "resource") {
                    setShowResourceDialog(true);
                  } else if (data.type === "risk") {
                    setShowRiskDialog(true);
                  } else if (data.type === "decision") {
                    setShowDecisionDialog(true);
                  } else if (data.type === "location") {
                    setShowAddressCard(true);
                  }
                }}
              >
                <div className="node-speeddial-root">
                  <button
                    type="button"
                    className="p-speeddial-button p-button p-button-icon-only node-speeddial-main-button node-type-icon-button"
                    aria-label={
                      data.type ? `Node type: ${data.type}` : "Node type"
                    }
                    tabIndex={-1}
                    onMouseDown={(e) => {
                      // Prevent focus/selection ring on click
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      // Ensure we never leave the button focused
                      (e.currentTarget as HTMLButtonElement).blur();
                    }}
                  >
                    {selectedSpeedDialIcon}
                  </button>
                </div>
              </div>
            )}

          {/* MS365 icon for MS365 nodes */}
          {isMS365Node && data.icon && (
            <div
              className="node-badge node-ms365-icon"
              onClick={() => {
                setSelectedNodeId(id);
                const node = getNode(id);
                if (node) setSelectedNode(node);
              }}
              onDoubleClick={(e) => {
                // Let it propagate to inputWrapper handler
              }}
            >
              {/^https?:\/\//.test(data.icon) ||
              data.icon.startsWith("data:image/") ? (
                <img
                  src={data.icon}
                  alt="ms365-icon"
                  className="node-ms365-icon-img"
                />
              ) : (
                <span className="node-ms365-icon-text">{data.icon}</span>
              )}
            </div>
          )}

          {/* Custom image/emoji icon */}
          {!isMS365Node && data.icon && data.icon.length > 0 ? (
            // Check if icon is a URL (http/https), a base64 string, or a file extension
            /^https?:\/\//.test(data.icon) ||
            data.icon.endsWith(".png") ||
            data.icon.endsWith(".jpg") ||
            data.icon.endsWith(".jpeg") ||
            data.icon.startsWith("data:image/") ? (
              <div
                className="node-badge custom-node-icon node-top-icon dragHandle"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <img
                  src={data.icon}
                  alt="node-icon"
                  className="node-top-icon-img"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  onClick={(e) => op.current?.toggle(e)}
                />
              </div>
            ) : (
              <div
                className="node-badge custom-node-icon node-top-icon node-top-icon-emoji-container dragHandle"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <span className="node-top-icon-emoji">{data.icon}</span>
              </div>
            )
          ) : null}

          {/* Info/notes icon */}
          {data.notes?.length > 0 && (
            <div
              className="node-badge node-notes-badge"
              onClick={handleDetailsClick}
              onDoubleClick={(e) => {
                e.stopPropagation();
                const node = getNode(id);
                if (node) setSelectedNode(node);
                setSelectedNodeId(id);
                setCurrentNodeLabel(data.label);
                setCurrentLinks(data.links || []);
                setCurrentNotes(data.notes);
                setShowNotesModal(true);
              }}
            >
              <LuPencil className="node-notes-icon" size={14} />
            </div>
          )}

          {/* Marker icon for nodes (complete, question, alert) */}
          {(data.marker || (data.complete && !data.marker)) &&
            !isDisabled &&
            !isMS365Node && (
              <div
                className={`node-badge node-marker-icon ${
                  data.marker === "alert"
                    ? "is-alert"
                    : data.marker === "question"
                      ? "is-question"
                      : data.marker === "strength"
                        ? "is-strength"
                        : data.marker === "weakness"
                          ? "is-weakness"
                          : data.marker === "opportunity"
                            ? "is-opportunity"
                            : data.marker === "threat"
                              ? "is-threat"
                      : "is-complete"
                }`}
                onClick={() => {
                  setSelectedNodeId(id);
                  const node = getNode(id);
                  if (node) setSelectedNode(node);
                }}
              >
                {data.marker === "question" ? (
                  "?"
                ) : data.marker === "alert" ? (
                  "!"
                ) : data.marker === "strength" ? (
                  <i className="pi pi-thumbs-up" />
                ) : data.marker === "weakness" ? (
                  <i className="pi pi-thumbs-down" />
                ) : data.marker === "opportunity" ? (
                  <i className="pi pi-bolt" />
                ) : data.marker === "threat" ? (
                  <i className="pi pi-exclamation-triangle" />
                ) : (
                  <LuCheck size={14} />
                )}
              </div>
            )}
        </div>

        {/* Overlay preview for the node's custom image, rendered outside the
            badges row so its (portal-based) DOM node can never influence the
            row's flex layout/height. */}
        {!isMS365Node &&
          data.icon &&
          data.icon.length > 0 &&
          (/^https?:\/\//.test(data.icon) ||
            data.icon.endsWith(".png") ||
            data.icon.endsWith(".jpg") ||
            data.icon.endsWith(".jpeg") ||
            data.icon.startsWith("data:image/")) && (
            <OverlayPanel ref={op}>
              <img src={data.icon} className="node-overlay-image" />
            </OverlayPanel>
          )}

        <div
          ref={wrapperRef}
          className={`inputWrapper${isDisabledByFilter ? " is-disabled-by-filter" : ""}`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div
            className="inputArea"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div
              id="node-input-field"
              className="node-input"
              node-identifier={id}
              contentEditable={data.userRole === "viewer" ? "false" : "true"}
              suppressContentEditableWarning
              spellCheck="false"
              onInput={handleChange}
              onPaste={(evt) => {
                if (data.userRole === "viewer") return;
                evt.preventDefault();
                const text = evt.clipboardData.getData("text/plain");
                document.execCommand("insertText", false, text);
              }}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              onKeyDown={handleKeyDown}
              onFocus={data.userRole === "viewer" ? undefined : handleFocus}
              onBlur={data.userRole === "viewer" ? undefined : handleBlur}
              onClick={() => setSelectedNodeId(id)}
              ref={inputRef}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />
            {/* Flag icon - positioned above node */}
            {/* {data.flagged &&
              data.flagged !== "none" &&
              (focusValue === "marker" || focusValue === "all") && (
                <div className="node-icons-row">
                  <IoFlag
                    className="node-flag-icon"
                    style={{
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      color:
                        data.flagged === "green"
                          ? "#8a9a5b"
                          : data.flagged === "yellow"
                          ? "#d2b773"
                          : data.flagged === "red"
                          ? "#b76e79"
                          : "#126656",
                    }}
                  />
                </div>
              )} */}
            {/* {(data.notes && data.notes.length > 0) || isNodeSelected ? (
            showDetails ? (
              <i
                className="pi pi-angle-down node-details"
                style={{
                  cursor: "pointer",
                  color:
                    data.notes && data.notes.length > 0
                      ? "black"
                      : undefined,
                }}
                onClick={handleDetailsClick}
              ></i>
            ) : (
              <i
                className="pi pi-angle-right node-details"
                style={{
                  cursor: "pointer",
                  color:
                    data.notes && data.notes.length > 0
                      ? "black"
                      : undefined,
                }}
                onClick={handleDetailsClick}
              ></i>
            )
          ) : null} */}
          </div>
        </div>

        {/* Properties display below node */}
        {activateShowValues &&
          (() => {
            const visibleProps =
              data.customProperties?.filter((prop) => prop.visible) || [];
            const rollupProps =
              doRollup && data.rollupValues ? data.rollupValues : [];
            const hasContent =
              visibleProps.length > 0 || rollupProps.length > 0;

            if (!hasContent) return null;

            return (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  marginTop: "4px",
                  padding: "4px 8px",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                  zIndex: 1,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                {/* Show rollup values when enabled */}
                {doRollup &&
                  rollupProps.map((rollup, idx) => (
                    <div
                      key={`rollup-${idx}`}
                      style={{
                        display: "flex",
                        gap: "4px",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontWeight: 600, color: "#666" }}>
                        {rollup.title}:
                      </span>
                      <span style={{ color: "#333" }}>{rollup.value}</span>
                    </div>
                  ))}
                {/* Show custom properties when not in rollup mode or in addition */}
                {!doRollup &&
                  visibleProps.map((prop, idx) => {
                    let displayValue: string;

                    if (prop.type === "calculated") {
                      const currency = useStore.getState().currency || "EUR";
                      const calculated = getCalculatedValue(
                        prop,
                        data.customProperties || [],
                        currency,
                      );
                      displayValue = calculated ? calculated.value : "0";
                    } else if (prop.type === "date" && prop.value) {
                      displayValue = new Date(prop.value).toLocaleDateString();
                    } else if (prop.type === "boolean") {
                      displayValue = prop.value ? "✓" : "✗";
                    } else {
                      displayValue = String(prop.value || "");
                    }

                    return (
                      <div
                        key={`prop-${idx}`}
                        style={{
                          display: "flex",
                          gap: "4px",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ fontWeight: 600, color: "#666" }}>
                          {prop.name}:
                        </span>
                        <span style={{ color: "#333" }}>{displayValue}</span>
                      </div>
                    );
                  })}
              </div>
            );
          })()}

        <div
          ref={noteDetailsRef}
          className={`node-details-area${showDetails ? " visible" : ""}`}
        >
          <div className="node-details-label">
            <ReactMarkdown
              components={{
                h1: ({ node, ...props }) => (
                  <h1 className="node-details-md-h1" {...props} />
                ),
                h2: ({ node, ...props }) => (
                  <h2 className="node-details-md-h2" {...props} />
                ),
                h3: ({ node, ...props }) => (
                  <h3 className="node-details-md-h3" {...props} />
                ),
                h4: ({ node, ...props }) => (
                  <h4 className="node-details-md-h4" {...props} />
                ),
                p: ({ node, ...props }) => (
                  <p className="node-details-md-p" {...props} />
                ),
                ul: ({ node, ...props }) => (
                  <ul className="node-details-md-ul" {...props} />
                ),
                ol: ({ node, ...props }) => (
                  <ol className="node-details-md-ol" {...props} />
                ),
                li: ({ node, ...props }) => (
                  <li className="node-details-md-li" {...props} />
                ),
                input: ({ node, ...props }) =>
                  props.type === "checkbox" ? (
                    <input className="node-details-md-checkbox" {...props} />
                  ) : (
                    <input {...props} />
                  ),
                table: () => null,
                thead: () => null,
                tbody: () => null,
                tr: () => null,
                th: () => null,
                td: () => null,
              }}
            >
              {convertHtmlToMarkdown(data.notes)}
            </ReactMarkdown>
          </div>
        </div>
      </div>

      {/* Task Dialog */}
      {showTaskDialog && (
        <KanbanCard
          visible={showTaskDialog}
          nodeTask={
            data.task || {
              id: `task-${id}`,
              title: data.label,
              nodeTitle: data.label,
              status: "todo",
            }
          }
          setShowKanbanCard={setShowTaskDialog}
          handleAddUpdateTask={handleTaskUpdate}
          handleDeleteTask={handleTaskDelete}
        />
      )}

      {/* Resource Dialog */}
      {showResourceDialog && (
        <ResourceDialog
          visible={showResourceDialog}
          resourceData={
            data.resource || {
              name: data.label,
              role: "",
              department: "",
              email: "",
              phone: "",
              availability: 100,
              skills: "",
              notes: "",
            }
          }
          setShowResourceDialog={setShowResourceDialog}
          handleSaveResource={handleResourceSave}
          handleDeleteResource={
            data.resource ? handleResourceDelete : undefined
          }
        />
      )}

      {/* Address/Location Card */}
      {showAddressCard && (
        <AddressCard
          visible={showAddressCard}
          nodeAddress={
            data.address || {
              id: id,
              nodeTitle: data.label,
              name: data.label,
              street: "",
              zip_code: "",
              city: "",
              Phone: "",
              email: "",
              vat: "",
              employees: "",
              notes: "",
            }
          }
          setShowAddressCard={setShowAddressCard}
          handleAddUpdateAddress={handleAddUpdateAddress}
          handleDeleteAddress={handleDeleteAddress}
        />
      )}

      {/* Risk Dialog */}
      {showRiskDialog && (
        <RiskDialog
          visible={showRiskDialog}
          riskData={
            data.risk || {
              name: data.label,
              description: "",
              category: "",
              impact: "Medium",
              likelihood: "Medium",
              severity: "Medium",
              mitigation: "",
              owner: "",
              status: "Open",
            }
          }
          setShowRiskDialog={setShowRiskDialog}
          handleSaveRisk={handleRiskSave}
          handleDeleteRisk={data.risk ? handleRiskDelete : undefined}
        />
      )}

      {/* Decision Dialog */}
      {showDecisionDialog && (
        <DecisionDialog
          visible={showDecisionDialog}
          decisionData={
            data.decision || {
              name: data.label,
              description: "",
              category: "",
              status: "Pending",
              decisionMaker: "",
              stakeholders: "",
              criteria: "",
              alternatives: "",
              selectedOption: "",
              rationale: "",
              implications: "",
            }
          }
          setShowDecisionDialog={setShowDecisionDialog}
          handleSaveDecision={handleDecisionSave}
          handleDeleteDecision={
            data.decision ? handleDecisionDelete : undefined
          }
        />
      )}

      {/* Image URL Dialog */}
      <Dialog
        visible={showImageDialog}
        modal
        header="Set Image URL"
        className="node-image-url-dialog"
        onHide={() => {
          setShowImageDialog(false);
          setImageUrl("");
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                setShowImageDialog(false);
                setImageUrl("");
              }}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              className="p-button-primary"
              onClick={handleImageUrlSave}
            />
          </div>
        }
      >
        <div className="node-image-url-dialog-body">
          <label htmlFor="imageUrl" className="node-image-url-label">
            Image URL:
          </label>
          <InputText
            id="imageUrl"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="node-image-url-input"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleImageUrlSave();
              }
            }}
          />
        </div>
      </Dialog>

      {/* Image Selection Menu */}
      <Menu ref={imageMenuRef} model={imageMenuItems} popup />

      {/* Unsplash Picker Dialog - Disabled due to props mismatch */}
      {/* <Dialog
        visible={showUnsplashPicker}
        onHide={() => setShowUnsplashPicker(false)}
        header="Select Image from Unsplash"
        style={{ width: "80vw", maxWidth: "800px" }}
        modal
      >
        <UnsplashImagePicker
          imagePickerVisible={showUnsplashPicker}
          selectedNode={{ id, data }}
          nodes={nodes}
          setNodes={() => {}}
          setIsDirty={() => {}}
          setImagePickerVisible={setShowUnsplashPicker}
        />
      </Dialog> */}

      {/* Emoji Picker Dialog - Disabled due to props mismatch */}
      {/* <Dialog
        visible={showEmojiPicker}
        onHide={() => setShowEmojiPicker(false)}
        header="Select Emoji"
        style={{ width: "400px" }}
        modal
      >
        <EmojiPickerPopup
          position={{ x: 0, y: 0 }}
          isDragging={false}
          onMouseDown={() => {}}
          onClose={() => setShowEmojiPicker(false)}
          onEmojiSelect={(emoji) => {
            updateNodeData(id, { icon: emoji });
            setShowEmojiPicker(false);
          }}
        />
      </Dialog> */}
    </>
  );
}

// Memo to prevent re-renders when only position changes (handled by ReactFlow CSS transforms)
export default memo(NodeItem, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.data === nextProps.data &&
    prevProps.selected === nextProps.selected &&
    prevProps.isConnectable === nextProps.isConnectable
  );
});
